/**
 * IFRAP-AI.fr - Open Data Hook
 * Hook React pour récupérer les KPIs réels depuis les APIs Open Data
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const OPENDATA_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useOpenDataKpis = () => {
  const [openDataKpis, setOpenDataKpis] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const fetchOpenDataKpis = useCallback(async (force = false) => {
    try {
      setLoading(true);
      setError(null);

      // Use the public dashboard endpoint (no auth required)
      const response = await api.get('/api/kpis/dashboard');
      
      if (response.data && response.data.categories) {
        const kpis = {};
        
        // Extract real KPIs from dashboard categories
        Object.values(response.data.categories).forEach(cat => {
          if (cat.kpis) {
            cat.kpis.forEach(kpi => {
              if (!kpi.is_mock) {
                kpis[kpi.code] = {
                  value: kpi.value,
                  period: kpi.period,
                  source: kpi.source,
                  isReal: true,
                  fetchedAt: kpi.collected_at || new Date().toISOString(),
                };
              }
            });
          }
        });

        setOpenDataKpis(kpis);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Error fetching Open Data KPIs:', err);
      setError(err.message || 'Erreur de connexion aux sources Open Data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchOpenDataKpis();
  }, [fetchOpenDataKpis]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOpenDataKpis();
    }, OPENDATA_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchOpenDataKpis]);

  /**
   * Fusionne les données statiques avec les données réelles Open Data
   * @param {Array} staticKpis - KPIs statiques depuis editorialKpis.js
   * @returns {Array} - KPIs enrichis avec données réelles si disponibles
   */
  const mergeWithStaticKpis = useCallback((staticKpis) => {
    return staticKpis.map(kpi => {
      const openData = openDataKpis[kpi.id];
      
      if (openData) {
        // Check if data is stale (period is > 9 months old for annual data, > 6 months for quarterly)
        let isStale = false;
        const period = openData.period || '';
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-11
        
        // Parse period like "2024", "T3 2025", "Oct 2025", "16 Mar 2026", "Fév 2026", etc.
        const yearMatch = period.match(/(\d{4})/);
        const quarterMatch = period.match(/T(\d)/i);
        const monthMatch = period.match(/(jan|fév|mar|avr|mai|jun|jul|aoû|sep|oct|nov|déc)/i);
        
        if (yearMatch) {
          const dataYear = parseInt(yearMatch[1]);
          
          if (quarterMatch) {
            // Quarterly data: stale if more than 3 quarters old (was 2)
            const dataQuarter = parseInt(quarterMatch[1]);
            const currentQuarter = Math.floor(currentMonth / 3) + 1;
            const quartersAgo = (currentYear - dataYear) * 4 + (currentQuarter - dataQuarter);
            isStale = quartersAgo > 3;
          } else if (monthMatch) {
            // Monthly data: stale if more than 4 months old (was 3)
            const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
            const dataMonth = months.findIndex(m => monthMatch[1].toLowerCase().startsWith(m));
            const monthsAgo = (currentYear - dataYear) * 12 + (currentMonth - dataMonth);
            isStale = monthsAgo > 4;
          } else {
            // Annual data: stale if 2+ years old only
            // Don't mark previous year as stale during current year
            isStale = dataYear < currentYear - 1;
          }
        }
        
        return {
          ...kpi,
          value: String(openData.value).replace('.', ','),
          status: isStale ? 'stale' : 'real',
          openDataSource: openData.source,
          openDataPeriod: openData.period,
          fetchedAt: openData.fetchedAt,
        };
      }
      
      return kpi;
    });
  }, [openDataKpis]);

  return {
    openDataKpis,
    loading,
    lastRefresh,
    error,
    refresh: fetchOpenDataKpis,
    mergeWithStaticKpis,
    hasRealData: Object.keys(openDataKpis).length > 0,
  };
};

export default useOpenDataKpis;
